import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  Stack,
  Tooltip,
  CircularProgress,
  Paper,
  InputAdornment,
  Fade,
  Avatar,
  Switch,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';
import {
  addCommas,
  digitsEnToFa,
  numberToWords,
} from '@persian-tools/persian-tools';
import {
  StorefrontTwoTone,
  CurrencyExchangeTwoTone,
  SaveRounded,
  PreviewRounded,
  PercentRounded,
  AttachMoneyRounded,
  TrendingUpRounded,
  TrendingDownRounded,
  SettingsSuggestRounded,
} from '@mui/icons-material';
import { services } from './services';
import { useToastContext } from '../../components/toast-context';

const GeneralSellAndBuybackSetting = ({
  type,
  setCurrentStratgy,
  trig,
  trigger,
  setConfigValues,
  setShowPreview,
}) => {
  const { showToast } = useToastContext();
  const isBuyback = type !== 'BUY';

  // کلید داینامیک: اگر بازخرید بود از 'buybackStrategy' و اگر فروش بود از 'strategy' استفاده کن
  const stratKey = isBuyback ? 'buybackStrategy' : 'strategy';

  const themefn = useTheme();

  // --- Modern Theme Configuration ---
  const theme = isBuyback
    ? {
        // ORANGE / AMBER (Buyback - Exchange/Trade)
        primary: '#ea580c', // Orange 600
        softBg: 'linear-gradient(145deg, #fff7ed 0%, #fff 100%)', // Orange 50 fade
        border: '#fed7aa', // Orange 200
        focusRing: 'rgba(234, 88, 12, 0.25)',
        shadow: '0 20px 40px -12px rgba(234, 88, 12, 0.15)',
        icon: (
          <CurrencyExchangeTwoTone sx={{ fontSize: 32, color: '#ea580c' }} />
        ),
        title: 'تنظیمات بازخرید طلا',
        subtitle: 'استراتژی خرید طلا از مشتری',
        trendIcon: (
          <TrendingDownRounded sx={{ color: '#f97316', opacity: 0.8 }} />
        ),
        chipBg: '#fff7ed',
        chipColor: '#c2410c',
      }
    : {
        // EMERALD / TEAL (Sell - Revenue)
        primary: '#059669', // Emerald 600
        softBg: 'linear-gradient(145deg, #ecfdf5 0%, #fff 100%)', // Emerald 50 fade
        border: '#a7f3d0', // Emerald 200
        focusRing: 'rgba(5, 150, 105, 0.25)',
        shadow: '0 20px 40px -12px rgba(5, 150, 105, 0.15)',
        icon: <StorefrontTwoTone sx={{ fontSize: 32, color: '#059669' }} />,
        title: 'تنظیمات فروش',
        subtitle: 'استراتژی فروش طلا به مشتری',
        trendIcon: (
          <TrendingUpRounded sx={{ color: '#10b981', opacity: 0.8 }} />
        ),
        chipBg: '#ecfdf5',
        chipColor: '#047857',
      };

  // --- States for Independent Price Source ---
  const [priceSource, setPriceSource] = useState('ESTJT');
  const [initialPriceSource, setInitialPriceSource] = useState('ESTJT');
  const [isSavingSource, setIsSavingSource] = useState(false);

  // --- States for Online Buyback Toggle ---
  const [isBuybackOnlineActive, setIsOnlineBuybackActive] = useState(false);
  const [isUpdatingOnlineBuyback, setIsUpdatingOnlineBuyback] = useState(false);
  const [priceSources, setPriceSources] = useState([]);

  const [initialValues, setInitialValues] = useState({
    [stratKey]: 'MANUAL',
    costPercentage: '',
    manualBasePrice: '',
  });

  const [estjPrice, setEstjPrice] = React.useState(null);
  const isMobile = useMediaQuery(themefn.breakpoints.down('sm'));

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { isSubmitting },
  } = useForm({
    defaultValues: {
      [stratKey]: '',
      costPercentage: '',
      manualBasePrice: '',
    },
  });

  const formValues = watch();

  const hasChanges =
    formValues[stratKey] !== initialValues[stratKey] ||
    formValues.costPercentage !== initialValues.costPercentage ||
    (formValues[stratKey] === 'MANUAL' &&
      formValues.manualBasePrice !== initialValues.manualBasePrice);

  const hasSourceChanges = priceSource !== initialPriceSource;

  useEffect(() => {
    setConfigValues(formValues);
  }, [
    formValues[stratKey],
    formValues.costPercentage,
    formValues.manualBasePrice,
  ]);

  // دریافت مستقل منبع قیمت‌گذاری از سرور
  useEffect(() => {
    services
      .getPriceSource()
      .then(res => {
        if (res?.nameEn) {
          setPriceSource(res.nameEn);
          setInitialPriceSource(res.nameEn);
        }
      })
      .catch(err => {
        console.error('Error loading price source:', err);
      });
  }, [trigger]);

  useEffect(() => {
    services.getEstjPrices().then(prices => {
      setEstjPrice({
        methghal: prices.methghal * 10,
        gram: prices.gold24k * 10,
      });
    });
  }, [trigger]);

  useEffect(() => {
    services.getProductsConfig({}).then(res => {
      const fetchedCost = res?.[0]?.costPercentage || res?.costPercentage || '';
      const fetchedStrategy = res?.[0]?.[stratKey] || res?.[stratKey] || '';
      const fetchedOnlineBuyback =
        res?.[0]?.isBuybackOnlineActive ?? res?.isBuybackOnlineActive ?? false;

      setIsOnlineBuybackActive(Boolean(fetchedOnlineBuyback));
      setValue('costPercentage', formValues.costPercentage || fetchedCost);

      if (fetchedStrategy) {
        setValue(stratKey, formValues[stratKey] || fetchedStrategy);
      }

      setInitialValues(prev => ({
        ...prev,
        costPercentage: fetchedCost,
        ...(fetchedStrategy && { [stratKey]: fetchedStrategy }),
      }));
    });
  }, [trigger]);

  const estjPricesText =
    estjPrice?.gram !== null ? digitsEnToFa(addCommas(estjPrice?.gram)) : '';

  const estjMethghalPricesText =
    estjPrice?.methghal !== null
      ? digitsEnToFa(addCommas(estjPrice?.methghal))
      : '';

  // هندلر تغییر مقدار لوکال سلکت‌باکس
  const handlePriceSourceChange = event => {
    setPriceSource(event.target.value);
  };

  // متد ذخیره سازی مستقل منبع قیمت‌گذاری
  const handleSavePriceSource = async () => {
    setIsSavingSource(true);
    try {
      await services.setPriceSource({ source: priceSource });
      setInitialPriceSource(priceSource);
      showToast('مرجع استعلام قیمت با موفقیت بروزرسانی شد.', 'success');
      trig();
    } catch (error) {
      console.error(error);
      showToast('خطا در ذخیره مرجع قیمت‌گذاری', 'error');
    } finally {
      setIsSavingSource(false);
    }
  };

  // متد جدید جهت تغییر وضعیت بازخرید آنلاین
  const handleOnlineBuybackToggle = async event => {
    const checked = event.target.checked;
    setIsUpdatingOnlineBuyback(true);
    try {
      await services.setProductConfig({
        isApplyingToAll: true,
        transactionType: type,
        isBuybackOnlineActive: checked,
      });
      setIsOnlineBuybackActive(checked);
      showToast('وضعیت بازخرید آنلاین با موفقیت به‌روزرسانی شد.', 'success');
      trig();
    } catch (error) {
      console.error('Error toggling online buyback:', error);
      showToast('خطا در تغییر وضعیت بازخرید آنلاین', 'error');
    } finally {
      setIsUpdatingOnlineBuyback(false);
    }
  };

  const onSubmit = async data => {
    if (formValues?.[stratKey] === 'MANUAL') {
      setShowPreview(true);
      return;
    }
    try {
      const hasConfigChanged =
        data[stratKey] !== initialValues[stratKey] ||
        data.costPercentage !== initialValues.costPercentage ||
        data.manualBasePrice !== initialValues.manualBasePrice;

      if (hasConfigChanged) {
        await services.setProductConfig({
          isApplyingToAll: true,
          transactionType: type,
          isBuybackOnlineActive: isBuybackOnlineActive,
          [stratKey]: data[stratKey],
          ...(data.costPercentage !== initialValues.costPercentage && {
            costPercentage: data.costPercentage,
          }),
        });
      }

      if (hasConfigChanged) {
        setInitialValues(prev => ({
          ...prev,
          [stratKey]: data[stratKey],
          costPercentage: data.costPercentage,
        }));
        setCurrentStratgy(data[stratKey]);
        showToast('با موفقیت ثبت شد.', 'success');
        trig();
      } else {
        showToast('هیچ تغییری برای ذخیره وجود ندارد.', 'info');
      }
    } catch (error) {
      console.log('the error', error);
      showToast('مشکلی پیش آمده', 'error');
    }
  };

  const manualPriceInWords = formValues.manualBasePrice
    ? (() => {
        const cleanedPrice = String(formValues.manualBasePrice).replace(
          /,/g,
          ''
        );
        const priceInTomans = Number(cleanedPrice) / 10;
        return priceInTomans > 0
          ? numberToWords(Math.floor(priceInTomans).toString())
          : '';
      })()
    : '';

  // --- Modern Input Styles ---
  const inputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '16px',
      backgroundColor: '#fff',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '& fieldset': {
        borderColor: '#e2e8f0',
        borderWidth: '1px',
      },
      '&:hover': {
        backgroundColor: '#fff',
        boxShadow: `0 4px 12px ${theme.focusRing}`,
        '& fieldset': { borderColor: theme.primary },
      },
      '&.Mui-focused': {
        backgroundColor: '#fff',
        boxShadow: `0 0 0 4px ${theme.focusRing}`,
        '& fieldset': { borderColor: theme.primary },
      },
    },
    '& .MuiInputLabel-root': {
      color: '#64748b',
      '&.Mui-focused': { color: theme.primary },
    },
  };

  useEffect(() => {
    services.getAvailablePriceSources().then(sources => {
      setPriceSources(sources);
    });
  }, [trigger]);

  return (
    <Box sx={{ width: '100%' }}>
      {/* --- Independent Price Source Section (Decoupled Card with Save Button) --- */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: '24px',
          border: '1px solid #e2e8f0',
          background: '#ffffff',
          boxShadow: '0 10px 30px -10px rgba(0,0,0,0.04)',
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'stretch', md: 'center' }}
          justifyContent="space-between"
          spacing={3}
        >
          {/* Info Side */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            sx={{ flexGrow: 1 }}
          >
            <Avatar
              variant="rounded"
              sx={{
                width: 48,
                height: 48,
                bgcolor: theme.chipBg,
                borderRadius: '12px',
                border: `1px solid ${theme.border}`,
              }}
            >
              <SettingsSuggestRounded sx={{ color: theme.primary }} />
            </Avatar>
            <Box>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 800, color: '#1e293b' }}
              >
                مرجع استعلام قیمت
              </Typography>
              <Typography variant="caption" sx={{ color: '#64748b' }}>
                مرجع دریافت نرخ زنده طلا برای کل سیستم
              </Typography>
            </Box>
          </Stack>

          {/* Action Side */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems="center"
            sx={{ width: { xs: '100%', md: 'auto' } }}
          >
            {/* Select Box */}
            <Box sx={{ width: { xs: '100%', sm: 220 } }}>
              <TextField
                select
                fullWidth
                value={priceSource}
                onChange={handlePriceSourceChange}
                disabled={isSavingSource}
                variant="outlined"
                sx={inputSx}
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      sx: {
                        borderRadius: '16px',
                        mt: 1,
                        boxShadow: theme.shadow,
                      },
                    },
                  },
                }}
              >
                {priceSources.map(source => (
                  <MenuItem value={source.nameEn}>{source.nameFa}</MenuItem>
                ))}
              </TextField>
            </Box>

            {/* Independent Save Button */}
            <Button
              variant="contained"
              disabled={isSavingSource || !hasSourceChanges}
              onClick={handleSavePriceSource}
              startIcon={
                isSavingSource ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <SaveRounded />
                )
              }
              sx={{
                height: 56,
                minWidth: 140,
                width: { xs: '100%', sm: 'auto' },
                borderRadius: '16px',
                backgroundColor: theme.primary,
                boxShadow: hasSourceChanges
                  ? `0 8px 20px -4px ${theme.primary}66`
                  : 'none',
                fontWeight: 700,
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: theme.primary,
                  transform: 'translateY(-2px)',
                  boxShadow: `0 12px 24px -4px ${theme.primary}88`,
                },
                '&.Mui-disabled': {
                  backgroundColor: '#e2e8f0',
                  color: '#94a3b8',
                  boxShadow: 'none',
                },
              }}
            >
              {isSavingSource ? '...' : 'ذخیره مرجع'}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* --- Main Configuration Form --- */}
      <Paper
        component="form"
        autoComplete="off"
        onSubmit={handleSubmit(onSubmit)}
        elevation={0}
        sx={{
          position: 'relative',
          width: '100%',
          p: { xs: 3, md: 4 },
          borderRadius: '24px',
          background: theme.softBg,
          border: `1px solid ${theme.border}`,
          boxShadow: theme.shadow,
          overflow: 'visible',
        }}
      >
        {/* --- Header Section --- */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          gap={2}
          mb={4}
        >
          <Stack direction="row" alignItems="center" spacing={2.5}>
            <Avatar
              variant="rounded"
              sx={{
                width: 56,
                height: 56,
                bgcolor: '#fff',
                boxShadow: '0 8px 16px rgba(0,0,0,0.04)',
                borderRadius: '16px',
                border: `1px solid ${theme.border}`,
              }}
            >
              {theme.icon}
            </Avatar>
            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,
                  color: '#1e293b',
                  letterSpacing: '-0.5px',
                  mb: 0.5,
                }}
              >
                {theme.title}
              </Typography>
            </Box>
          </Stack>

          {/* Header Controls (Online Buyback Toggle & Price Status) */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
          >
            {/* دکمه سوئیتچ بازخرید آنلاین (مخصوص صفحه بازخرید) */}
            {isBuyback && (
              <Paper
                elevation={0}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 2,
                  py: 0.75,
                  borderRadius: '16px',
                  bgcolor: '#ffffff',
                  border: `1px solid ${theme.border}`,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 700, color: '#1e293b' }}
                >
                  بازخرید آنلاین
                </Typography>
                {isUpdatingOnlineBuyback ? (
                  <CircularProgress
                    size={20}
                    sx={{ color: theme.primary, mx: 1 }}
                  />
                ) : (
                  <Switch
                    checked={isBuybackOnlineActive}
                    onChange={handleOnlineBuybackToggle}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: theme.primary,
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track':
                        {
                          backgroundColor: theme.primary,
                        },
                    }}
                  />
                )}
              </Paper>
            )}

            {/* Status Badge */}
            <Stack gap={0}>
              {estjPrice?.gram && (
                <Stack
                  direction="row"
                  spacing={isMobile ? 0.5 : 1}
                  alignItems="center"
                >
                  <Typography
                    variant={isMobile ? 'caption' : 'body2'}
                    sx={{
                      color: '#4b5563',
                      fontWeight: 400,
                      fontSize: isMobile ? '0.7rem' : '0.85rem',
                    }}
                  >
                    گرم مرجع قیمت:
                  </Typography>
                  <Typography
                    variant={isMobile ? 'caption' : 'body2'}
                    sx={{
                      fontWeight: 600,
                      color: '#111827',
                      fontSize: isMobile ? '0.7rem' : '0.9rem',
                    }}
                  >
                    {estjPricesText}
                  </Typography>
                </Stack>
              )}

              {estjPrice?.methghal && (
                <Stack
                  direction="row"
                  spacing={isMobile ? 0.5 : 1}
                  alignItems="center"
                >
                  <Typography
                    variant={isMobile ? 'caption' : 'body2'}
                    sx={{
                      color: '#4b5563',
                      fontWeight: 400,
                      fontSize: isMobile ? '0.7rem' : '0.85rem',
                    }}
                  >
                    مظنه مرجع قیمت:
                  </Typography>
                  <Typography
                    variant={isMobile ? 'caption' : 'body2'}
                    sx={{
                      fontWeight: 600,
                      color: '#111827',
                      fontSize: isMobile ? '0.7rem' : '0.9rem',
                    }}
                  >
                    {estjMethghalPricesText}
                  </Typography>
                </Stack>
              )}
            </Stack>
          </Stack>
        </Stack>

        {/* --- Form Grid --- */}
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={3}
          alignItems="flex-start"
        >
          {/* 1. Strategy Selector */}
          <Box sx={{ flex: 1, minWidth: { md: 280 }, width: '100%' }}>
            <Controller
              name={stratKey}
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  fullWidth
                  label="روش قیمت‌گذاری"
                  variant="outlined"
                  sx={inputSx}
                  SelectProps={{
                    MenuProps: {
                      PaperProps: {
                        sx: {
                          borderRadius: '16px',
                          mt: 1,
                          boxShadow: theme.shadow,
                        },
                      },
                    },
                  }}
                >
                  <MenuItem value="MANUAL">دستی</MenuItem>
                  {type === 'BUY' && (
                    <MenuItem value="AUTOMATIC_COMPETITOR">
                      بر اساس رقیب
                    </MenuItem>
                  )}
                  <MenuItem value="AUTOMATIC_ESTJT">
                    بر اساس مظنه مرجع قیمت
                  </MenuItem>
                </TextField>
              )}
            />
          </Box>

          {/* 2. Dynamic Input Area (Price or Percentage) */}
          <Box sx={{ flex: 1.5, width: '100%', position: 'relative' }}>
            {formValues?.[stratKey] === 'AUTOMATIC_ESTJT' && !isBuyback && (
              <Fade in={true} timeout={500}>
                <Box>
                  <Controller
                    name="costPercentage"
                    control={control}
                    rules={{
                      required: 'درصد هزینه تولید الزامی است',
                      min: {
                        value: 0.01,
                        message: 'مقدار باید بزرگتر از صفر باشد',
                      },
                      validate: value =>
                        value > 0 || 'مقدار نمی‌تواند صفر باشد',
                    }}
                    render={({ field, fieldState }) => (
                      <NumericFormat
                        {...field}
                        customInput={TextField}
                        fullWidth
                        thousandSeparator=","
                        label="درصد هزینه تولید"
                        placeholder="مثلا: 7"
                        variant="outlined"
                        sx={inputSx}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PercentRounded sx={{ color: theme.primary }} />
                            </InputAdornment>
                          ),
                        }}
                        isAllowed={values => {
                          const { floatValue } = values;
                          return floatValue === undefined || floatValue > 0;
                        }}
                        onValueChange={values => {
                          const { floatValue } = values;
                          field.onChange(floatValue ?? '');
                        }}
                        value={field.value}
                      />
                    )}
                  />
                </Box>
              </Fade>
            )}

            {formValues?.[stratKey] === 'MANUAL' && (
              <Fade in={true} timeout={500}>
                <Box>
                  <Controller
                    name="manualBasePrice"
                    control={control}
                    render={({ field }) => (
                      <NumericFormat
                        {...field}
                        customInput={TextField}
                        fullWidth
                        thousandSeparator=","
                        label="قیمت پایه (ریال)"
                        placeholder="قیمت را وارد کنید..."
                        variant="outlined"
                        sx={inputSx}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <AttachMoneyRounded
                                sx={{ color: theme.primary }}
                              />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <Typography
                              variant="caption"
                              sx={{ color: '#94a3b8', mx: 1 }}
                            >
                              RIAL
                            </Typography>
                          ),
                        }}
                      />
                    )}
                  />

                  {/* Floating "Price in Words" Bubble */}
                  {manualPriceInWords && (
                    <Fade in={true}>
                      <Box
                        sx={{
                          mt: 2,
                          p: 1.5,
                          borderRadius: '12px',
                          background: isBuyback ? '#fef2f2' : '#ecfdf5',
                          border: `1px dashed ${theme.primary}`,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, color: theme.primary }}
                        >
                          معادل:
                        </Typography>
                        <Typography variant="body2" color="text.primary">
                          {manualPriceInWords} تومان
                        </Typography>
                      </Box>
                    </Fade>
                  )}
                </Box>
              </Fade>
            )}
          </Box>

          {/* 3. Action Button */}
          <Box
            sx={{ width: { xs: '100%', lg: 'auto' }, minWidth: 180, pt: 0.5 }}
          >
            <Tooltip
              title={
                !hasChanges && formValues?.[stratKey] !== 'MANUAL'
                  ? 'تغییری وجود ندارد'
                  : ''
              }
            >
              <span>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={
                    isSubmitting ||
                    (!isBuyback &&
                      !hasChanges &&
                      formValues?.[stratKey] !== 'MANUAL') ||
                    (formValues?.[stratKey] === 'MANUAL' &&
                      !formValues.manualBasePrice)
                  }
                  startIcon={
                    isSubmitting ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : formValues?.[stratKey] === 'MANUAL' ? (
                      <PreviewRounded />
                    ) : (
                      <SaveRounded />
                    )
                  }
                  sx={{
                    height: 56,
                    borderRadius: '16px',
                    backgroundColor: theme.primary,
                    boxShadow: `0 8px 20px -4px ${theme.primary}66`,
                    fontSize: '1rem',
                    fontWeight: 700,
                    textTransform: 'none',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      backgroundColor: theme.primary,
                      transform: 'translateY(-2px)',
                      boxShadow: `0 12px 24px -4px ${theme.primary}88`,
                    },
                    '&.Mui-disabled': {
                      backgroundColor: '#e2e8f0',
                      color: '#94a3b8',
                      boxShadow: 'none',
                    },
                  }}
                >
                  {isSubmitting
                    ? '...'
                    : formValues?.[stratKey] === 'MANUAL'
                      ? 'مشاهده پیش‌نمایش'
                      : 'ذخیره تغییرات'}
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
};

export default GeneralSellAndBuybackSetting;
